import { Fragment, useState } from "react";

import MaticWordmarkFooter from "@assets/svg/MaticWordmarkFooter";
import Eyebrow from "@/blueprint/components/Eyebrow/Eyebrow";
import FooterLink from "@/chrome/SiteFooter/FooterLink";
import LiveStat from "@/blueprint/components/LiveStat/LiveStat";
import PaymentMarks from "@/chrome/SiteFooter/PaymentMarks";
import Text from "@/blueprint/components/Text/Text";
import { FOCUS_REVEALED_CLASSES } from "@/blueprint/components/VisuallyHidden/VisuallyHidden";
import {
  FOOTER_COLUMNS,
  FOOTER_STATS,
  NEWSLETTER,
} from "@/chrome/chromeContent";
import SubscribeInput from "@/components/SubscribeInput";
import { FOOTER_COOKIE_PREFERENCES_LABEL } from "@/consent/consentContent";
import { useConsentStore } from "@/consent/useConsentStore";
import { cn } from "@/lib/utils";
import { useTrack } from "@/analytics/events";

export type FooterTone =
  | "butter"
  | "orange"
  | "lavender"
  | "neutral"
  | "black"
  | "teal"
  | "olive"
  | "maroon";

const TONE_CLASSES: Record<FooterTone, string> = {
  butter: "bg-brand-butter text-brand-black",
  orange: "bg-brand-orange text-brand-black",
  lavender: "bg-brand-lavender text-brand-black",
  neutral: "bg-neutral2 text-brand-black",
  black: "bg-brand-black text-brand-white",
  teal: "bg-brand-teal text-brand-white",
  olive: "bg-brand-olive text-brand-white",
  maroon: "bg-brand-maroon text-brand-white",
};

type SiteFooterProps = {
  tone?: FooterTone | undefined;
  /** The live counters, on unless a page asks for a quieter close
   *  (/serene). */
  stats?: boolean | undefined;
};

const SiteFooter = ({ tone = "teal", stats = true }: SiteFooterProps) => {
  const [statsPaused, setStatsPaused] = useState(false);
  const track = useTrack();
  const openConsentPrefs = useConsentStore((s) => s.openPrefs);

  return (
    <footer className="px-grid-margin pb-grid-margin">
      <div
        className={cn(
          "relative flex w-full flex-col gap-double overflow-hidden rounded-l p-card-lg pb-0",
          TONE_CLASSES[tone],
        )}
      >
        {stats && (
          <button
            type="button"
            aria-pressed={statsPaused}
            onClick={() => setStatsPaused((paused) => !paused)}
            className={FOCUS_REVEALED_CLASSES}
          >
            Pause live counters
          </button>
        )}
        {/* justify-between rather than Figma's fixed 80 gap: the live counters
            are narrower than drawn, and the fixed gap strands the row. */}
        <div className="flex flex-col gap-[20px] tab:gap-[24px] sd:flex-row sd:items-start sd:justify-between sd:gap-[40px]">
          {/* The counters' numerals overhang their line-height; the bottom
              padding reserves it so the gap above the payment row is visible. */}
          {stats && (
            <div className="flex flex-col pb-[20px] tab:pb-[28px] sd:pb-0">
              {FOOTER_STATS.map((stat) => (
                <div
                  key={stat.id}
                  className="flex h-[var(--text-h1--line-height)] items-baseline gap-xs"
                >
                  <LiveStat
                    seedValue={stat.seedValue}
                    perSecond={stat.perSecond}
                    seedTimeMs={stat.seedTimeMs}
                    srLabel={stat.srLabel}
                    isRevealed
                    paused={statsPaused}
                    // Trim Mono (monospace) keeps digit width fixed so the live
                    // counter doesn't jitter as values tick up; PP Mori is proportional.
                    className="font-trimMono text-h1 font-semibold tabular-nums"
                  />
                  <Eyebrow variant="eyebrow-sm" className="text-inherit">
                    {stat.label}
                  </Eyebrow>
                </div>
              ))}
            </div>
          )}
          <PaymentMarks />
        </div>
        <div className="grid grid-cols-2 gap-x-s gap-y-xxl tab:grid-cols-4 tab:gap-x-m tab:gap-y-3xl sd:grid-cols-8 sd:gap-x-grid-gutter sd:gap-y-half">
          {FOOTER_COLUMNS.map((column) => (
            <div key={column.title} className="flex flex-col gap-xs sd:gap-m">
              <p className="font-ppMori text-footer-title font-semibold">
                {column.title}
              </p>
              <div className="flex flex-col gap-1 sd:gap-xs">
                {column.links.map((link, linkIndex) => (
                  <Fragment key={link.href}>
                    <FooterLink
                      href={link.href}
                      external={link.external === true}
                      onClick={() =>
                        track("footer_link_clicked", {
                          href: link.href,
                          label: link.label,
                          column: column.title,
                          is_external: link.external === true,
                        })
                      }
                    >
                      {link.label}
                    </FooterLink>
                    {column.title === "Privacy" && linkIndex === 0 && (
                      <FooterLink
                        onClick={() => {
                          openConsentPrefs();
                          track("footer_link_clicked", {
                            href: "#cookie-settings",
                            label: FOOTER_COOKIE_PREFERENCES_LABEL,
                            column: column.title,
                            is_external: false,
                          });
                        }}
                      >
                        {FOOTER_COOKIE_PREFERENCES_LABEL}
                      </FooterLink>
                    )}
                  </Fragment>
                ))}
              </div>
            </div>
          ))}
          <div className="relative top-[-13px] col-span-full flex flex-col gap-m tab:top-[-5px] sd:top-0 sd:col-span-3 sd:col-start-6 sd:row-start-1 [&_.h-px]:bg-current [&_input]:placeholder:text-inherit [&_svg]:text-inherit">
            <SubscribeInput
              source="stay_updated"
              inputClassName="text-paragraph-md tracking-normal text-inherit caret-inherit"
              arrowClassName="text-inherit"
            />
            <Text variant="paragraph-sm" tone="inherit" className="opacity-70">
              {NEWSLETTER.disclaimer}
            </Text>
          </div>
          {/* Fixed per breakpoint and ending at the card's edge: the crop the
              card's old fixed heights gave, independent of the content above. */}
          <div className="col-span-full -mt-[13px] h-[119.486px] overflow-hidden tab:-mt-[5px] tab:h-[238.207px] sd:mt-0 sd:h-[192.814px] d:h-[274px]">
            <div className="overflow-hidden [aspect-ratio:1475/422] sd:[aspect-ratio:1057/197]">
              <MaticWordmarkFooter classes="h-full w-full sd:h-auto" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
